import moment from 'moment';
import { useContext, useMemo, useRef } from 'react';

import { useExportSalePlansToExcelByYearMutation } from '@app/api/SalePlans';
import {
  WARNING,
  RIGHTS_IS_CREATE,
  RIGHTS_IS_EXPORT,
  RIGHTS_IS_ON,
  RIGHTS_SALE_PLANS,
} from '@app/constants';
import { XLSX } from '@app/constants/fileMimeTypes';
import { useAccessRights, useUserPrint } from '@app/hooks';
import { HeaderContext, NotificationContext } from '@app/providers';
import { handleCatchError } from '@app/services';

export const useSalePlansPageHeader = () => {
  const { hasUserRight: hasUserRightForModuleView } = useAccessRights(
    RIGHTS_SALE_PLANS,
    RIGHTS_IS_ON
  );
  const { hasUserRight: hasUserRightToCreateSalePlans } = useAccessRights(
    RIGHTS_SALE_PLANS,
    RIGHTS_IS_CREATE
  );
  const { hasUserRight: hasUserRightToExportSalePlans } = useAccessRights(
    RIGHTS_SALE_PLANS,
    RIGHTS_IS_EXPORT
  );
  const linkContainerRef = useRef<HTMLDivElement | null>(null);
  const { activeBranchId } = useUserPrint();
  const { showNotification } = useContext(NotificationContext);
  const { header } = useContext(HeaderContext);
  const [exportSalePlansToExcelByYear, { isLoading }] =
    useExportSalePlansToExcelByYearMutation();
  const year = useMemo(
    () => (header?.year as number) || moment().year(),
    [header?.year]
  );

  const downloadFileHandler = async () => {
    try {
      const { Name: fileName, Data: fileContent } =
        await exportSalePlansToExcelByYear({
          ActiveBranchId: activeBranchId,
          year,
        }).unwrap();

      const link = document.createElement('a');

      link.setAttribute('id', 'sale-plans-export');
      link.setAttribute('download', fileName ?? '');
      link.setAttribute('href', `data:${XLSX};base64,${fileContent ?? ''}`);
      linkContainerRef.current?.appendChild(link);

      link.click();
      link.remove();
    } catch (error) {
      showNotification(handleCatchError(error), WARNING);
    }
  };

  return {
    hasUserRightToExportSalePlans,
    hasUserRightToCreateSalePlans,
    hasUserRightForModuleView,
    downloadFileHandler,
    isLoading,
    linkContainerRef,
  };
};
